<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { formatTime } from '@/utils/datetime'
import { API, HOST } from '@/utils/http'
import { booleanPointInPolygon, point } from '@turf/turf'
import MapSource from '@/components/map/MapSource.vue'
import MapLayer from '@/components/map/MapLayer.vue'
import MapLayerPopup from '@/components/map/MapLayerPopup.vue'
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel'
import {  Image, ImageOff } from 'lucide-vue-next'

const props = defineProps({
  track: {
    type: Object,
    required: true
  },
  code: {
    type: String,
    required: true
  },
  expanded: {
    type: Boolean,
    default: false
  },
  layerId: {
    type: String,
    required: true
  },
  map: {
    type: Object,
    required: true
  },
  testBorderGeometry: {
    type: Object,
    required: true
  },
  isVisible: {
    type: Boolean,
    default: true
  },
  activePopupTrackId: {
    type: [Number, String, null],
    default: null
  }
})

const emit = defineEmits(['toggle', 'popup-opened', 'popup-closed'])
const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])

const photos = ref([])
const photosLoaded = ref(false)
const isLoadingPhotos = ref(false)

const hasPhotos = computed(() => props.track.has_photos === 1)

const popupCoordinates = ref(null)
const popupCounter = ref(0)

const isPopupVisible = computed(() => {
  return props.activePopupTrackId === props.track.id
})

const trackPointGeometry = computed(() => {
  const coordinates = [props.track.track_detection.lon, props.track.track_detection.lat]
  const trackPoint = point(coordinates);

  let isInside = false;
  if (props.testBorderGeometry.features && props.testBorderGeometry.features.length > 0) {
    for (const feature of props.testBorderGeometry.features) {
      if (booleanPointInPolygon(trackPoint, feature)) {
        isInside = true;
        break;
      }
    }
  }

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates
      },
      properties: {
        heading: props.track.track_detection.heading || 0,
        id: props.track.id,
        radar_track_id: props.track.radar_track_id,
        isInside
      }
    }]
  }
})

const trackLinesGeometry = computed(() => {
  const outsideLines = {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: props.track.track_detection.track_line.geometry,
      properties: {
        id: props.track.id,
        radar_track_id: props.track.radar_track_id
      }
    }]
  };

  const insideLines = {
    type: 'FeatureCollection',
    features: []
  };

  const coordinates = props.track.track_detection.track_line.geometry.coordinates;

  for (let i = 1; i < coordinates.length; i++) {
    const startPoint = point(coordinates[i-1]);
    const endPoint = point(coordinates[i]);

    let startInside = false;
    let endInside = false;

    for (const feature of props.testBorderGeometry.features) {
      if (booleanPointInPolygon(startPoint, feature)) {
        startInside = true;
      }
      if (booleanPointInPolygon(endPoint, feature)) {
        endInside = true;
      }
    }

    if (startInside && endInside) {
      insideLines.features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [coordinates[i-1], coordinates[i]]
        },
        properties: {
          id: props.track.id,
          radar_track_id: props.track.radar_track_id
        }
      });
    }
  }

  return {
    outsideLines,
    insideLines
  };
})

const emblaMainApi = ref(null)
const emblaThumbnailApi = ref(null)
const selectedPhotoIndex = ref(0)

function onSelect() {
  if (!emblaMainApi.value || !emblaThumbnailApi.value)
    return

  selectedPhotoIndex.value = emblaMainApi.value.selectedScrollSnap()
  emblaThumbnailApi.value.scrollTo(selectedPhotoIndex.value)

  nextTick(() => {
    selectedPhotoIndex.value = emblaMainApi.value.selectedScrollSnap()
  })
}

function onThumbClick(index) {
  if (!emblaMainApi.value)
    return
  emblaMainApi.value.scrollTo(index)
}

watch(emblaMainApi, (api) => {
  if (!api) return

  selectedPhotoIndex.value = api.selectedScrollSnap() || 0

  api.on('select', onSelect)
  api.on('reInit', onSelect)

  onMounted(() => {
    if (api) {
      onSelect()
    }
  })
}, { immediate: true })

const loadPhotos = async () => {
  if (!hasPhotos.value || photosLoaded.value || isLoadingPhotos.value) return

  isLoadingPhotos.value = true

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/layers/${props.code}/radar/tracks/${props.track.id}/photos`
    })

    if (response.data && response.data.photos) {
      // Sort photos in descending order by created_at timestamp (newest first)
      photos.value = response.data.photos.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at)
      })
    }
    photosLoaded.value = true
  } catch (e) {
    console.error(`Error fetching photos for track ${props.track.id}:`, e)
  } finally {
    isLoadingPhotos.value = false
  }
}

const handleToggle = (event) => {
  emit('toggle', { track: props.track, open: event.target.open })

  if (event.target.open && hasPhotos.value && !photosLoaded.value) {
    loadPhotos()
  }
}

const handleTrackClick = (e) => {
  if (isPopupVisible.value) {
    emit('popup-closed')
    return
  }

  popupCoordinates.value = [props.track.track_detection.lat, props.track.track_detection.lon]
  popupCounter.value++

  emit('popup-opened', props.track.id)

  if (props.track.has_photos === 1 && !photosLoaded.value) {
    loadPhotos()
  }
}

const closePopup = () => {
  emit('popup-closed')
}

watch(() => props.expanded, (newVal) => {
  if (newVal && hasPhotos.value && !photosLoaded.value) {
    loadPhotos()
  }
}, { immediate: true })
</script>

<template>
  <div>
    <!-- Regular track lines (outside border) - visible when not highlighted -->
    <MapSource
      v-if="isVisible && trackLinesGeometry && !isPopupVisible"
      :map="map"
      type="geojson"
      :id="`${layerId}-track-${track.id}-lines-source`"
      :data="trackLinesGeometry.outsideLines"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-track-${track.id}-lines-layer`"
        type="line"
        :source="`${layerId}-track-${track.id}-lines-source`"
        :options="{
          paint: {
            'line-color': '#62748e',
            'line-width': 3,
            'line-opacity': 0.7,
            'line-dasharray': [2, 1]
          }
        }"
        @click="handleTrackClick"
      />
    </MapSource>

    <!-- Highlighted track lines (outside border) - visible when highlighted -->
    <MapSource
      v-if="isVisible && trackLinesGeometry && isPopupVisible"
      :map="map"
      type="geojson"
      :id="`${layerId}-track-${track.id}-highlighted-lines-source`"
      :data="trackLinesGeometry.outsideLines"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-track-${track.id}-highlighted-lines-layer`"
        type="line"
        :source="`${layerId}-track-${track.id}-highlighted-lines-source`"
        :options="{
          paint: {
            'line-color': '#0059FF',
            'line-width': 4,
            'line-opacity': 0.9,
            'line-dasharray': [2, 1]
          }
        }"
        @click="handleTrackClick"
      />
    </MapSource>

    <!-- Regular track lines (inside border) - visible when not highlighted -->
    <MapSource
      v-if="isVisible && trackLinesGeometry && trackLinesGeometry.insideLines.features.length > 0 && !isPopupVisible"
      :map="map"
      type="geojson"
      :id="`${layerId}-track-${track.id}-inside-lines-source`"
      :data="trackLinesGeometry.insideLines"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-track-${track.id}-inside-lines-layer`"
        type="line"
        :source="`${layerId}-track-${track.id}-inside-lines-source`"
        :options="{
          paint: {
            'line-color': '#ffa500',
            'line-width': 3,
            'line-opacity': 0.8,
            'line-dasharray': [2, 1]
          }
        }"
        @click="handleTrackClick"
      />
    </MapSource>

    <!-- Highlighted track lines (inside border) - visible when highlighted -->
    <MapSource
      v-if="isVisible && trackLinesGeometry && trackLinesGeometry.insideLines.features.length > 0 && isPopupVisible"
      :map="map"
      type="geojson"
      :id="`${layerId}-track-${track.id}-highlighted-inside-lines-source`"
      :data="trackLinesGeometry.insideLines"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-track-${track.id}-highlighted-inside-lines-layer`"
        type="line"
        :source="`${layerId}-track-${track.id}-highlighted-inside-lines-source`"
        :options="{
          paint: {
            'line-color': '#0059FF',
            'line-width': 4,
            'line-opacity': 1,
            'line-dasharray': [2, 1]
          }
        }"
        @click="handleTrackClick"
      />
    </MapSource>

    <!-- Regular track points - visible when not highlighted -->
    <MapSource
      v-if="isVisible && trackPointGeometry && !isPopupVisible"
      :map="map"
      type="geojson"
      :id="`${layerId}-track-${track.id}-points-source`"
      :data="trackPointGeometry"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-track-${track.id}-points-layer`"
        type="symbol"
        :source="`${layerId}-track-${track.id}-points-source`"
        :options="{
          layout: {
            'icon-image': [
              'case',
              ['get', 'isInside'],
              'arrow-primary-icon',
              'arrow-icon'
            ],
            'icon-size': 0.15,
            'icon-rotate': ['get', 'heading'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true
          },
          paint: {
            'icon-color': '#888888',
            'icon-opacity': 0.9
          }
        }"
        @click="handleTrackClick"
      >
      </MapLayer>
    </MapSource>

    <!-- Highlighted track points - visible when highlighted -->
    <MapSource
      v-if="isVisible && trackPointGeometry && isPopupVisible"
      :map="map"
      type="geojson"
      :id="`${layerId}-track-${track.id}-highlighted-points-source`"
      :data="trackPointGeometry"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-track-${track.id}-highlighted-points-layer`"
        type="symbol"
        :source="`${layerId}-track-${track.id}-highlighted-points-source`"
        :options="{
          layout: {
            'icon-image': 'arrow-primary-icon',
            'icon-size': 0.18,
            'icon-rotate': ['get', 'heading'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true
          },
          paint: {
            'icon-color': '#0059FF',
            'icon-opacity': 1
          }
        }"
        @click="handleTrackClick"
      >
        <MapLayerPopup
          v-if="isPopupVisible && popupCoordinates"
          :map="map"
          :coordinates="popupCoordinates"
          :show="isPopupVisible"
          :offset="10"
          :max-width="'500px'"
          :min-width="'400px'"
          @close="closePopup"
          :key="`track-${track.id}-popup-${popupCounter}`"
          :className="'radar-track-popup'"
        >
          <div class="text-[14px] font-sans px-3 py-2 min-w-[300px]">
            <div class="">
              <h3 class="font-bold text-lg">#{{ track.radar_track_id }}</h3>
              <ul class="max-w-[275px]">
                <li class="flex justify-between">
                  <span class="font-medium text-gray-500">Start:</span>
                  <span>{{ formatTime(track.start_time, settings.tz, 'MMM DD, YYYY HH:mm') }}</span>
                </li>
                <li class="flex justify-between">
                  <span class="font-medium text-gray-500">Last update:</span>
                  <span>{{ formatTime(track.last_update, settings.tz, 'MMM DD, YYYY HH:mm') }}</span>
                </li>
                <li class="flex justify-between">
                  <span class="font-medium text-gray-500">Speed:</span>
                  <span>{{ track.track_detection.speed }} kts</span>
                </li>
                <li class="flex justify-between">
                  <span class="font-medium text-gray-500">Travel distance:</span>
                  <span>{{ track.running_distance.toFixed(2) }} km</span>
                </li>
              </ul>
            </div>

            <div v-if="track.has_photos === 1 && photos.length > 0" class="mt-2">
              <h4 class="font-bold mb-1">Photos</h4>
              <div v-if="!photosLoaded" class="text-center py-2">
                <span class="text-gray-500">Loading photos...</span>
              </div>
              <div v-else class="relative">
                <!-- Main Carousel -->
                <Carousel
                  class="w-full mb-2"
                  @init-api="(val) => emblaMainApi = val"
                  @select="onSelect"
                >
                  <CarouselContent>
                    <CarouselItem v-for="photo in photos" :key="photo.id">
                      <a :href="photo.s3_photo_link" target="_blank" class="block">
                        <img
                          :src="photo.s3_photo_link"
                          alt="Track photo"
                          class="w-full aspect-16/9 object-cover rounded shadow cursor-pointer hover:opacity-90"
                        />
                      </a>
                    </CarouselItem>
                  </CarouselContent>
                  <CarouselPrevious class="absolute top-1/2 left-2 -translate-y-1/2" />
                  <CarouselNext class="absolute top-1/2 right-2 -translate-y-1/2" />
                </Carousel>

                <!-- Thumbnail Carousel -->
                <Carousel
                  v-if="photos.length > 1"
                  class="w-full"
                  @init-api="(val) => emblaThumbnailApi = val"
                >
                  <CarouselContent class="flex gap-1 ml-0">
                    <CarouselItem
                      v-for="(photo, index) in photos"
                      :key="photo.id"
                      class="pl-0 basis-1/5 cursor-pointer"
                      @click="onThumbClick(index)"
                    >
                      <div
                        class="p-1 transition-all duration-200 transform"
                      >
                        <img
                          :src="photo.s3_photo_link"
                          alt="Thumbnail"
                          class="w-full aspect-square object-cover rounded"
                          :class="[
                            index === selectedPhotoIndex
                              ? 'outline-2 outline-primary rounded scale-105'
                              : 'opacity-60 hover:opacity-80'
                          ]"
                        />
                        <div class="text-xs text-white absolute bottom-1 left-1 right-1 text-center bg-black/20 rounded m-0.5 px-1 py-0.5">
                          {{ formatTime(photo.created_at, settings.tz, 'HH:mm:ss') }}
                        </div>
                      </div>
                    </CarouselItem>
                  </CarouselContent>
                </Carousel>
              </div>
            </div>
          </div>
        </MapLayerPopup>
      </MapLayer>
    </MapSource>

    <div class="text-gray-600 mb-0.5">
      <div
        @click.stop="handleTrackClick"
        class="flex items-center justify-between cursor-pointer font-medium p-0.5 transition-all duration-200"
        :class="isPopupVisible ? 'bg-primary/10 rounded shadow-sm border-l-4 border-primary' : 'hover:bg-gray-100 rounded'"
      >
        <div class="flex items-center ml-0.5">
          <span
            class="cursor-pointer"
            :class="isPopupVisible ? 'text-primary font-bold' : 'text-dark'"
          >#{{ track.radar_track_id }}</span>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <span class="text-gray-600">{{ track.track_detection.speed }} kts</span>
            <span class="">{{ track.running_distance.toFixed(2) }} km</span>
            <span class="">{{ formatTime(track.last_update, settings.tz, 'HH:mm') }}</span>

            <Image v-if="track.has_photos === 1"
              class="w-4 h-4"
              title="Has photos" />
            <ImageOff v-else
              class="w-4 h-4 text-gray-300"
              title="No photos" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Target mapbox popup close button */
.radar-track-popup .mapboxgl-popup-close-button {
  font-size: 20px;
  width: 26px;
  height: 26px;
  padding: 0;
  right: 2px;
  top: 2px;
  border-radius: 50%;
}

.radar-track-popup .mapboxgl-popup-close-button span {
  line-height: 23px;
  display: block;
  height: 26px;
}

.radar-track-popup .mapboxgl-popup-close-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.radar-track-popup {
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
}

.radar-track-popup .mapboxgl-popup-content {
  padding: 0;
  border-radius: 8px;
}
</style>
